import {
  CORNER_ENUM_KEYS,
  CORNER_ENUM_KEY_VALUES,
  LEGACY_CORNER_ENUM_ALIASES,
  TYPOGRAPHY_ENUM_KEYS,
  TYPOGRAPHY_ENUM_KEY_VALUES,
  type CornerEnumKey,
  type CornerTokenMap,
  type TypographyEnumKey,
  type TypographyPresetTokens,
} from './types';

/**
 * Shared corner presets — sharp → subtle → rounded → pill.
 *
 * Token roles (site Border radius dial):
 * - `sm` → fields, tabs, compact chrome (never a stadium under Pill)
 * - `cta` → buttons / CTAs — true capsule only on Pill
 * - `md` → surfaces / media frames — soft corners; never a capsule on large photos
 * - `pill` → always-capsule chrome (chips, gallery caption); sharp → 0
 */
export const CORNER_PRESETS = {
  [CORNER_ENUM_KEYS.sharp]: { sm: '0', md: '0', pill: '0', cta: '0' },
  [CORNER_ENUM_KEYS.subtle]: {
    sm: '0.25rem',
    md: '0.5rem',
    pill: '999px',
    cta: '0.25rem',
  },
  [CORNER_ENUM_KEYS.rounded]: {
    sm: '0.5rem',
    md: '0.875rem',
    pill: '999px',
    cta: '0.5rem',
  },
  [CORNER_ENUM_KEYS.pill]: {
    sm: '0.75rem',
    md: '1.25rem',
    pill: '999px',
    cta: '999px',
  },
} as const satisfies Record<CornerEnumKey, CornerTokenMap>;

/** Shared typography presets extracted from theme personalities. */
export const TYPOGRAPHY_PRESETS = {
  [TYPOGRAPHY_ENUM_KEYS.editorialSerif]: {
    headingFont: 'Playfair Display',
    bodyFont: 'Inter',
    scaleFactor: 1.25,
  },
  [TYPOGRAPHY_ENUM_KEYS.bistroWarm]: {
    headingFont: 'Fraunces',
    bodyFont: 'Source Sans 3',
    scaleFactor: 1.2,
  },
  /** Sushi-bar / counter: quiet mincho display + geometric JP-aware sans — not Playfair/Inter. */
  [TYPOGRAPHY_ENUM_KEYS.counterPrecision]: {
    headingFont: 'Shippori Mincho',
    bodyFont: 'Zen Kaku Gothic New',
    scaleFactor: 1.12,
  },
  /** Vietnam neighborhood — Be Vietnam Pro display + Source Sans (DE/VI friendly). */
  [TYPOGRAPHY_ENUM_KEYS.herbSteam]: {
    headingFont: 'Be Vietnam Pro',
    bodyFont: 'Source Sans 3',
    scaleFactor: 1.18,
  },
  /** Vietnam tiled shop — geometric Syne + Be Vietnam Pro body (not herb-steam pairing). */
  [TYPOGRAPHY_ENUM_KEYS.tileAwning]: {
    headingFont: 'Syne',
    bodyFont: 'Be Vietnam Pro',
    scaleFactor: 1.1,
  },
  /** Pan-Asian court — soft serif + Noto Sans (not sushi mincho pair). */
  [TYPOGRAPHY_ENUM_KEYS.lanternCourt]: {
    headingFont: 'Literata',
    bodyFont: 'Noto Sans',
    scaleFactor: 1.16,
  },
  [TYPOGRAPHY_ENUM_KEYS.limestoneOrangery]: {
    headingFont: 'Newsreader',
    bodyFont: 'Figtree',
    scaleFactor: 1.28,
  },
  [TYPOGRAPHY_ENUM_KEYS.moorlandHearth]: {
    headingFont: 'Spectral',
    bodyFont: 'Schibsted Grotesk',
    scaleFactor: 1.14,
  },
  [TYPOGRAPHY_ENUM_KEYS.brutalGrotesk]: {
    headingFont: 'Archivo Black',
    bodyFont: 'IBM Plex Sans',
    scaleFactor: 1.15,
  },
  [TYPOGRAPHY_ENUM_KEYS.earthOrganic]: {
    headingFont: 'Lora',
    bodyFont: 'Nunito Sans',
    scaleFactor: 1.2,
  },
  [TYPOGRAPHY_ENUM_KEYS.neonDisplay]: {
    headingFont: 'Bebas Neue',
    bodyFont: 'DM Sans',
    scaleFactor: 1.2,
  },
  [TYPOGRAPHY_ENUM_KEYS.vintageSerif]: {
    headingFont: 'Cormorant Garamond',
    bodyFont: 'Libre Franklin',
    scaleFactor: 1.22,
  },
  [TYPOGRAPHY_ENUM_KEYS.streetBold]: {
    headingFont: 'Oswald',
    bodyFont: 'Work Sans',
    scaleFactor: 1.18,
  },
  [TYPOGRAPHY_ENUM_KEYS.mustardRail]: {
    headingFont: 'Bebas Neue',
    bodyFont: 'IBM Plex Sans',
    scaleFactor: 1.16,
  },
  [TYPOGRAPHY_ENUM_KEYS.tahiniWindow]: {
    headingFont: 'Fraunces',
    bodyFont: 'Nunito Sans',
    scaleFactor: 1.14,
  },
  [TYPOGRAPHY_ENUM_KEYS.cleanSans]: {
    headingFont: 'Outfit',
    bodyFont: 'Inter',
    scaleFactor: 1.2,
  },
} as const satisfies Record<TypographyEnumKey, TypographyPresetTokens>;

export function isCornerEnumKey(value: string): value is CornerEnumKey {
  return (CORNER_ENUM_KEY_VALUES as readonly string[]).includes(value);
}

/** Maps current + legacy corner ids onto the three unified presets. */
export function normalizeCornerEnumKey(value: string | null | undefined): CornerEnumKey | null {
  if (!value) return null;
  if (isCornerEnumKey(value)) return value;
  const legacy = LEGACY_CORNER_ENUM_ALIASES[value as keyof typeof LEGACY_CORNER_ENUM_ALIASES];
  return legacy ?? null;
}

export function isTypographyEnumKey(value: string): value is TypographyEnumKey {
  return (TYPOGRAPHY_ENUM_KEY_VALUES as readonly string[]).includes(value);
}

export function resolveCornerPreset(cornerEnum: CornerEnumKey): CornerTokenMap {
  return CORNER_PRESETS[cornerEnum];
}

export function resolveTypographyPreset(typographyEnum: TypographyEnumKey): TypographyPresetTokens {
  return TYPOGRAPHY_PRESETS[typographyEnum];
}

/** All Google Font family names used by typography presets (for preview loading). */
export function collectTypographyPresetFontFamilies(): string[] {
  const families = new Set<string>();
  for (const preset of Object.values(TYPOGRAPHY_PRESETS)) {
    families.add(preset.headingFont);
    families.add(preset.bodyFont);
  }
  return [...families];
}

export function buildGoogleFontsStylesheetHref(families: string[]): string | null {
  if (families.length === 0) return null;
  const query = families
    .map((name) => `family=${encodeURIComponent(name).replace(/%20/g, '+')}`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
}
